import { apiRequest } from "./api";

const pageLimit = 100;
const installationStatuses = new Set(["active", "suspended", "uninstalled"]);

function requiredID(value, label) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    throw new Error(`${label}不能为空`);
  }
  return encodeURIComponent(normalized);
}

function commandIdempotencyKey(prefix) {
  const cryptoAPI = typeof window !== "undefined" ? window.crypto : null;
  const randomPart = cryptoAPI && typeof cryptoAPI.randomUUID === "function"
    ? cryptoAPI.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomPart}`;
}

async function listAllPages(pathForCursor) {
  const items = [];
  const seenCursors = new Set();
  let after = "";
  let hasMore = true;

  while (hasMore) {
    const payload = await apiRequest(pathForCursor(after));
    if (payload && Array.isArray(payload.items)) {
      items.push(...payload.items);
    }
    hasMore = Boolean(payload && payload.has_more);
    if (!hasMore) {
      return items;
    }

    const nextAfter = String(payload.next_after || "").trim();
    if (!nextAfter || seenCursors.has(nextAfter)) {
      throw new Error("应用列表分页游标无效");
    }
    seenCursors.add(nextAfter);
    after = nextAfter;
  }
  return items;
}

export async function listInstalledApplications(spaceID) {
  const tenantID = requiredID(spaceID, "空间 ID");
  return listAllPages((after) => (
    `/api/v1/platform/installations?tenant_id=${tenantID}&limit=${pageLimit}`
      + (after ? `&after=${encodeURIComponent(after)}` : "")
  ));
}

export async function listInstallationCredentials(installationID) {
  const id = requiredID(installationID, "Installation ID");
  return listAllPages((after) => (
    `/api/v1/platform/credentials?installation_id=${id}&limit=${pageLimit}`
      + (after ? `&after=${encodeURIComponent(after)}` : "")
  ));
}

export async function getInstallationUsageSummary(installationID) {
  const id = requiredID(installationID, "Installation ID");
  return apiRequest(`/api/v1/platform/installations/${id}/usage-summary`);
}

export async function changeInstallationStatus(installationID, status, options = {}) {
  const id = requiredID(installationID, "Installation ID");
  const normalizedStatus = String(status || "").trim().toLowerCase();
  if (!installationStatuses.has(normalizedStatus)) {
    throw new Error("Installation 状态无效");
  }
  const idempotencyKey = String(
    options.idempotencyKey || commandIdempotencyKey("installation-status"),
  ).trim();
  const payload = await apiRequest(`/api/v1/platform/installations/${id}/status`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ status: normalizedStatus }),
  });
  return payload && payload.installation ? payload.installation : null;
}

export async function loadSpaceApplications(spaceID) {
  const installations = await listInstalledApplications(spaceID);
  return Promise.all(installations.map(async (installation) => {
    const [usage, credentials] = await Promise.all([
      getInstallationUsageSummary(installation.id),
      listInstallationCredentials(installation.id),
    ]);
    return { ...installation, usage, credentials };
  }));
}
